/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  getStructDecoder,
  getStructEncoder,
  getU8Decoder,
  getU8Encoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
  type WritableSignerAccount,
} from '@solana/kit';
import { STAKE_POOL_PROGRAM_ADDRESS } from '../programs';
import { getAccountMetaFactory, type ResolvedAccount } from '../shared';

export const INITIALIZE_PROGRAM_AUTHORITY_DISCRIMINATOR = 8;

export function getInitializeProgramAuthorityDiscriminatorBytes() {
  return getU8Encoder().encode(INITIALIZE_PROGRAM_AUTHORITY_DISCRIMINATOR);
}

export type InitializeProgramAuthorityInstruction<
  TProgram extends string = typeof STAKE_POOL_PROGRAM_ADDRESS,
  TAccountProgramAuthority extends string | AccountMeta<string> = string,
  TAccountInitialAuthority extends string | AccountMeta<string> = string,
  TAccountPayer extends string | AccountMeta<string> = string,
  TAccountSystemProgram extends
    | string
    | AccountMeta<string> = '11111111111111111111111111111111',
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountProgramAuthority extends string
        ? WritableAccount<TAccountProgramAuthority>
        : TAccountProgramAuthority,
      TAccountInitialAuthority extends string
        ? ReadonlySignerAccount<TAccountInitialAuthority> &
            AccountSignerMeta<TAccountInitialAuthority>
        : TAccountInitialAuthority,
      TAccountPayer extends string
        ? WritableSignerAccount<TAccountPayer> &
            AccountSignerMeta<TAccountPayer>
        : TAccountPayer,
      TAccountSystemProgram extends string
        ? ReadonlyAccount<TAccountSystemProgram>
        : TAccountSystemProgram,
      ...TRemainingAccounts,
    ]
  >;

export type InitializeProgramAuthorityInstructionData = {
  discriminator: number;
};

export type InitializeProgramAuthorityInstructionDataArgs = {};

export function getInitializeProgramAuthorityInstructionDataEncoder(): FixedSizeEncoder<InitializeProgramAuthorityInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([['discriminator', getU8Encoder()]]),
    (value) => ({
      ...value,
      discriminator: INITIALIZE_PROGRAM_AUTHORITY_DISCRIMINATOR,
    })
  );
}

export function getInitializeProgramAuthorityInstructionDataDecoder(): FixedSizeDecoder<InitializeProgramAuthorityInstructionData> {
  return getStructDecoder([['discriminator', getU8Decoder()]]);
}

export function getInitializeProgramAuthorityInstructionDataCodec(): FixedSizeCodec<
  InitializeProgramAuthorityInstructionDataArgs,
  InitializeProgramAuthorityInstructionData
> {
  return combineCodec(
    getInitializeProgramAuthorityInstructionDataEncoder(),
    getInitializeProgramAuthorityInstructionDataDecoder()
  );
}

export type InitializeProgramAuthorityInput<
  TAccountProgramAuthority extends string = string,
  TAccountInitialAuthority extends string = string,
  TAccountPayer extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  /** The program authority PDA to create */
  programAuthority: Address<TAccountProgramAuthority>;
  /** The initial authority who will control authorized creators */
  initialAuthority: TransactionSigner<TAccountInitialAuthority>;
  /** The account paying for rent */
  payer: TransactionSigner<TAccountPayer>;
  /** The system program */
  systemProgram?: Address<TAccountSystemProgram>;
};

export function getInitializeProgramAuthorityInstruction<
  TAccountProgramAuthority extends string,
  TAccountInitialAuthority extends string,
  TAccountPayer extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address = typeof STAKE_POOL_PROGRAM_ADDRESS,
>(
  input: InitializeProgramAuthorityInput<
    TAccountProgramAuthority,
    TAccountInitialAuthority,
    TAccountPayer,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress }
): InitializeProgramAuthorityInstruction<
  TProgramAddress,
  TAccountProgramAuthority,
  TAccountInitialAuthority,
  TAccountPayer,
  TAccountSystemProgram
> {
  // Program address.
  const programAddress = config?.programAddress ?? STAKE_POOL_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    programAuthority: {
      value: input.programAuthority ?? null,
      isWritable: true,
    },
    initialAuthority: {
      value: input.initialAuthority ?? null,
      isWritable: false,
    },
    payer: { value: input.payer ?? null, isWritable: true },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Resolve default values.
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      '11111111111111111111111111111111' as Address<'11111111111111111111111111111111'>;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.programAuthority),
      getAccountMeta(accounts.initialAuthority),
      getAccountMeta(accounts.payer),
      getAccountMeta(accounts.systemProgram),
    ],
    data: getInitializeProgramAuthorityInstructionDataEncoder().encode({}),
    programAddress,
  } as InitializeProgramAuthorityInstruction<
    TProgramAddress,
    TAccountProgramAuthority,
    TAccountInitialAuthority,
    TAccountPayer,
    TAccountSystemProgram
  >);
}

export type ParsedInitializeProgramAuthorityInstruction<
  TProgram extends string = typeof STAKE_POOL_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    /** The program authority PDA to create */
    programAuthority: TAccountMetas[0];
    /** The initial authority who will control authorized creators */
    initialAuthority: TAccountMetas[1];
    /** The account paying for rent */
    payer: TAccountMetas[2];
    /** The system program */
    systemProgram: TAccountMetas[3];
  };
  data: InitializeProgramAuthorityInstructionData;
};

export function parseInitializeProgramAuthorityInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>
): ParsedInitializeProgramAuthorityInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 4) {
    // TODO: Coded error.
    throw new Error('Not enough accounts');
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      programAuthority: getNextAccount(),
      initialAuthority: getNextAccount(),
      payer: getNextAccount(),
      systemProgram: getNextAccount(),
    },
    data: getInitializeProgramAuthorityInstructionDataDecoder().decode(
      instruction.data
    ),
  };
}
